
/**
 * Deep learning forecasting models for financial analysis
 */

import { DeepLearningConfig, ForecastResult } from './types';
import { 
  extractTimeSeriesFeatures, 
  calculateCrossValidationAccuracy, 
  calculateFeatureImportance 
} from './technical-indicators';

export function deepLearningForecast(
  historicalData: number[],
  config: DeepLearningConfig,
  assumptions: any
): ForecastResult {
  const { lookbackPeriod, forecastHorizon, modelType } = config;
  
  // Prepare feature matrix for deep learning
  const features = extractTimeSeriesFeatures(historicalData, lookbackPeriod);
  
  // Apply different models based on configuration
  let forecast: number[] = [];
  let confidence: number[] = [];
  
  switch (modelType) {
    case 'lstm':
      ({ forecast, confidence } = lstmForecast(features, forecastHorizon, assumptions));
      break;
    case 'transformer':
      ({ forecast, confidence } = transformerForecast(features, forecastHorizon, assumptions));
      break;
    case 'ensemble':
      ({ forecast, confidence } = ensembleForecast(features, forecastHorizon, assumptions));
      break;
  }
  
  // Calculate model accuracy using cross-validation
  const modelAccuracy = calculateCrossValidationAccuracy(historicalData, features);
  
  // Feature importance analysis
  const featureImportance = calculateFeatureImportance(features, historicalData);
  
  // Generate scenario analysis with worst case
  const scenarios = generateAdvancedScenariosWithWorstCase(forecast, confidence, assumptions);
  
  return {
    forecast,
    confidence,
    modelAccuracy,
    featureImportance,
    scenarios
  };
}

function lstmForecast(features: number[][], horizon: number, assumptions: any): { forecast: number[]; confidence: number[] } {
  // Simplified LSTM-inspired forecasting
  const forecast: number[] = [];
  const confidence: number[] = [];
  
  if (features.length === 0) {
    return { forecast: Array(horizon).fill(0), confidence: Array(horizon).fill(0.3) };
  }
  
  const lastFeature = features[features.length - 1];
  const baseValue = lastFeature[0]; // mean value
  const trend = lastFeature[2];
  const volatility = lastFeature[6];
  
  for (let i = 0; i < horizon; i++) {
    // Apply LSTM-like state transitions
    const timeDecay = Math.exp(-i * 0.1);
    const trendComponent = trend * (i + 1) * timeDecay;
    const seasonalComponent = Math.sin((i * 2 * Math.PI) / 12) * baseValue * 0.1;
    const noiseComponent = (Math.random() - 0.5) * volatility * 0.1;
    
    const predictedValue = baseValue + trendComponent + seasonalComponent + noiseComponent;
    const confidenceValue = Math.max(0.3, Math.min(0.95, timeDecay * 0.8));
    
    forecast.push(Math.max(0, predictedValue));
    confidence.push(confidenceValue);
  }
  
  return { forecast, confidence };
}

function transformerForecast(features: number[][], horizon: number, assumptions: any): { forecast: number[]; confidence: number[] } {
  // Simplified transformer-inspired attention mechanism
  const forecast: number[] = [];
  const confidence: number[] = [];
  
  if (features.length === 0) {
    return { forecast: Array(horizon).fill(0), confidence: Array(horizon).fill(0.3) };
  }
  
  // Attention weights for different time steps
  const attentionWeights = features.map((_, i) => Math.exp(-(features.length - i - 1) * 0.2));
  const totalWeight = attentionWeights.reduce((sum, w) => sum + w, 0);
  const normalizedWeights = attentionWeights.map(w => w / totalWeight);
  
  // Weighted feature combination
  const weightedFeatures = features[0].map((_, featureIndex) => 
    features.reduce((sum, feature, timeIndex) => 
      sum + feature[featureIndex] * normalizedWeights[timeIndex], 0)
  );
  
  const baseValue = weightedFeatures[0];
  const trend = weightedFeatures[2];
  const volatility = weightedFeatures[6];
  
  for (let i = 0; i < horizon; i++) {
    const attentionDecay = Math.exp(-i * 0.05);
    const predictedValue = baseValue + trend * (i + 1) * attentionDecay;
    const confidenceValue = Math.max(0.4, Math.min(0.9, attentionDecay * 0.85));
    
    forecast.push(Math.max(0, predictedValue));
    confidence.push(confidenceValue);
  }
  
  return { forecast, confidence };
}

function ensembleForecast(features: number[][], horizon: number, assumptions: any): { forecast: number[]; confidence: number[] } {
  // Combine multiple models
  const lstm = lstmForecast(features, horizon, assumptions);
  const transformer = transformerForecast(features, horizon, assumptions);
  
  // Ensemble with weighted averaging
  const forecast = lstm.forecast.map((value, i) => 
    (value * 0.6 + transformer.forecast[i] * 0.4));
  const confidence = lstm.confidence.map((value, i) => 
    Math.max(value, transformer.confidence[i]));
  
  return { forecast, confidence };
}

function generateAdvancedScenariosWithWorstCase(forecast: number[], confidence: number[], assumptions: any): any {
  return {
    optimistic: forecast.map((value, i) => value * (1 + confidence[i] * 0.3)),
    realistic: forecast,
    pessimistic: forecast.map((value, i) => value * (1 - confidence[i] * 0.2)),
    worstCase: forecast.map((value, i) => value * (1 - confidence[i] * 0.4)) // More aggressive downside
  };
}
